import { useId, useMemo, useState } from "react";
import { parseNumbers, stats } from "@/lib/math/education";
import { SciStatGrid } from "@/lib/science/sci-ui";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";

export function StatsCalc() {
  const locale = getLocale();
  const id = useId();
  const [input, setInput] = useState("12, 15, 15, 18, 21, 24, 24, 24, 30");

  const out = useMemo(() => stats(parseNumbers(input)), [input]);

  const num = (n: number) =>
    n.toLocaleString(locale, { maximumFractionDigits: 3 });

  return (
    <div className="sci-card rounded-2xl space-y-4 p-6 sm:p-8">
      <div className="space-y-1.5">
        <label htmlFor={id} className="block text-sm text-muted-foreground">
          {m.st_input()}
        </label>
        <textarea
          id={id}
          rows={4}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder={m.st_placeholder()}
          className="w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-sm text-neutral-900"
        />
      </div>

      {out ? (
        <>
          <div className="sci-result-zone flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.st_mean()}
            </span>
            <span
              data-testid="st-result"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {num(out.mean)}
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.st_count(), value: num(out.count) },
              { label: m.st_median(), value: num(out.median) },
              {
                label: m.st_mode(),
                value: out.mode.length ? out.mode.map(num).join(", ") : "—",
              },
              { label: m.st_min(), value: num(out.min) },
              { label: m.st_max(), value: num(out.max) },
              { label: m.st_range(), value: num(out.range) },
              { label: m.st_stddev(), value: num(out.stdDev) },
              { label: m.st_variance(), value: num(out.variance) },
              { label: m.st_sum(), value: num(out.sum) },
            ]}
          />
        </>
      ) : (
        <p className="text-sm text-muted-foreground">{m.st_hint()}</p>
      )}
    </div>
  );
}
